import React, { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SideMenu from '../components/SideMenu';
import OrderDetails from './OrderDetails';
import { useTheme } from '../context/ThemeContext';
import { useOrders } from '../context/OrdersContext';
import './webhooks.css';

export default function CalculationDetailsPage() {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentTheme, theme } = useTheme();
  const { deleteCalculationPayload } = useOrders();

  const [deleteAction, setDeleteAction] = useState({
    canDelete: false,
    payloadId: null,
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const openDeleteConfirm = useCallback(() => {
    if (!deleteAction.canDelete || deleteSubmitting) return;
    setDeleteError('');
    setDeleteConfirmOpen(true);
  }, [deleteAction.canDelete, deleteSubmitting]);

  const executeDelete = useCallback(async () => {
    if (!deleteAction.payloadId || deleteSubmitting) return;

    setDeleteSubmitting(true);
    setDeleteError('');
    try {
      const result = await deleteCalculationPayload(deleteAction.payloadId);
      if (result?.ok) {
        setDeleteConfirmOpen(false);
        navigate('/calculations');
        return;
      }
      setDeleteError(t(result?.error || 'ordersDeleteFailed'));
    } finally {
      setDeleteSubmitting(false);
    }
  }, [deleteAction.payloadId, deleteSubmitting, deleteCalculationPayload, navigate, t]);

  return (
    <div
      className="webhooks-page"
      style={{
        '--wh-background': currentTheme.background,
        '--wh-surface': currentTheme.surface,
        '--wh-border': currentTheme.border,
        '--wh-card-shadow': currentTheme.cardShadow,
        '--wh-text': currentTheme.text,
        '--wh-text-secondary': currentTheme.textSecondary,
      }}
    >
      <SideMenu />
      <main className="webhooks-main calculation-details-page">
        <header className="calculation-details-header">
          <button
            type="button"
            onClick={() => navigate('/calculations')}
            aria-label="Back to calculations"
          >
            ←
          </button>
          <div>
            <p>CALCULATION</p>
            <h1>{orderNumber}</h1>
          </div>
          <div className="calculation-details-headerActions">
            {deleteAction.canDelete && (
              <button
                type="button"
                className={[
                  'webhooks-dangerBtn',
                  theme,
                  deleteSubmitting ? 'disabled' : 'enabled',
                ].join(' ')}
                onClick={openDeleteConfirm}
                disabled={deleteSubmitting}
              >
                <span className="orders-createShipmentBtnContent">
                  <span className="oi oi-warning" aria-hidden="true" />
                  {deleteSubmitting ? t('Deleting') : t('Delete')}
                </span>
              </button>
            )}
            <button
              type="button"
              className="copy-link-button"
              onClick={() => navigator.clipboard.writeText(window.location.href)}
            >
              Copy link
            </button>
          </div>
        </header>

        {deleteError && (
          <div className={['webhooks-error', theme, 'webhooks-sectionGap'].join(' ')}>
            {deleteError}
          </div>
        )}

        <OrderDetails
          order={{ order_number: orderNumber }}
          onDeleteAction={setDeleteAction}
        />
      </main>

      {deleteConfirmOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-calculation-warning-title"
          className={['webhooks-modalOverlay', theme].join(' ')}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !deleteSubmitting) {
              setDeleteConfirmOpen(false);
            }
          }}
        >
          <div className="webhooks-modal">
            <div className="webhooks-modalHeader">
              <div
                className="webhooks-modalTitle orders-createShipmentWarningTitle"
                id="delete-calculation-warning-title"
              >
                <span className="oi oi-warning" aria-hidden="true" />
                {t('ordersDeleteWarningTitle')}
              </div>
              <button
                type="button"
                onClick={() => setDeleteConfirmOpen(false)}
                className={['webhooks-iconBtn', theme].join(' ')}
                aria-label={t('Cancel')}
                title={t('Cancel')}
                disabled={deleteSubmitting}
              >
                ×
              </button>
            </div>
            <div className="webhooks-modalBody">
              <p className="orders-createShipmentWarningMessage">
                {t('ordersDeleteWarningMessage')}
              </p>
              {deleteError && (
                <div className={['webhooks-error', theme].join(' ')}>{deleteError}</div>
              )}
            </div>
            <div className="webhooks-modalFooter">
              <button
                type="button"
                className={[
                  'webhooks-dangerBtn',
                  theme,
                  deleteSubmitting ? 'disabled' : 'enabled',
                ].join(' ')}
                onClick={executeDelete}
                disabled={deleteSubmitting}
              >
                <span className="orders-createShipmentBtnContent">
                  <span className="oi oi-warning" aria-hidden="true" />
                  {deleteSubmitting ? t('Deleting') : t('ordersDeleteWarningProceed')}
                </span>
              </button>
              <button
                type="button"
                className={[
                  'webhooks-secondaryBtn',
                  theme,
                  deleteSubmitting ? 'disabled' : 'enabled',
                ].join(' ')}
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={deleteSubmitting}
              >
                {t('ordersDeleteWarningAbort')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
